/**
 * Les invitations d'acteurs CHEZ L'EMPLOYEUR — DRH, élus du CSE, auditeur.
 *
 * À ne pas confondre avec `organisations` : celles-là invitent un collaborateur
 * dans un CABINET. Ici on invite quelqu'un dans l'entreprise cliente, avec un
 * rôle que la matrice de C2 connaît.
 */
import axios from 'axios';
import { APP_CONFIG } from '@/constants/app';
import { tokenManager } from '@/utils/token-manager';

const api = axios.create({
  baseURL: APP_CONFIG.api.baseUrl,
  headers: { 'Content-Type': 'application/json' },
});

// Le gestionnaire de jetons du produit, comme tous les autres services. Ma
// première version relisait `localStorage` à la main et ne trouvait rien : la
// page « Mon entreprise » s'affichait vide alors que l'API répondait
// correctement. Réinventer la lecture du jeton, c'est se tromper une fois de
// plus que nécessaire.
tokenManager.setupInterceptors(api);

/** Les rôles tenables chez un employeur, dans les mots des utilisateurs. */
export const ROLES_EMPLOYEUR = [
  {
    valeur: 'admin',
    libelle: 'Direction',
    aide: "Administre l'entreprise et ses accès",
  },
  {
    valeur: 'drh',
    libelle: 'Direction des ressources humaines',
    aide: 'Voit les personnes, pas la facturation',
  },
  {
    valeur: 'manager',
    libelle: "Responsable d'équipe",
    aide: 'Limité à son périmètre',
  },
  {
    valeur: 'cse',
    libelle: 'Élu du CSE',
    aide: 'BDESE agrégée, sans donnée nominative',
  },
  {
    valeur: 'auditor',
    libelle: 'Auditeur',
    aide: 'Lecture seule, pour un contrôle',
  },
  { valeur: 'reader', libelle: 'Lecteur', aide: 'Consultation simple' },
] as const;

export type RoleEmployeur = (typeof ROLES_EMPLOYEUR)[number]['valeur'];

/**
 * Qualité que l'API peut rendre pour l'espace personnel.
 *
 * `salarie` n'est volontairement PAS un rôle invitable : il vient d'un lien
 * d'emploi vérifié par le serveur, jamais d'une invitation employeur.
 */
export type RoleChezEmployeur = RoleEmployeur | 'salarie';

export function libelleDuRole(valeur: string): string {
  return ROLES_EMPLOYEUR.find(r => r.valeur === valeur)?.libelle ?? valeur;
}

export interface InvitationEmployeur {
  uuid: string;
  email: string;
  role: RoleEmployeur;
  etat: 'pending' | 'accepted' | 'revoked';
  expireLe: string;
  accepteeLe?: string | null;
}

/** Ce que le porteur du lien voit — volontairement peu de choses. */
export interface InvitationLue {
  email: string;
  role: RoleEmployeur;
  expireLe: string;
}

function extraire<T>(reponse: { data: unknown }): T {
  // L'API enveloppe ses réponses dans `{ statusCode, data }`.
  const corps = reponse.data as { data?: T };
  return (corps?.data ?? (corps as unknown)) as T;
}

export interface MonEmployeur {
  uuid: string;
  nom: string;
  /** Rôle principal conservé par l'API pour les anciens clients. */
  role: RoleChezEmployeur;
  /** Toutes les qualités réelles quand une personne en cumule plusieurs. */
  roles?: RoleChezEmployeur[];
  /** Le dossier qui porte cette entreprise chez son cabinet. */
  folderUuid?: string | null;
  /**
   * Ce que le cabinet a ouvert à cette entreprise.
   *
   * Sans cette information, la barre latérale ne peut pas savoir s'il faut
   * proposer « Absences » — et un dirigeant à qui l'on active le SIRH ne verrait
   * toujours rien. Le serveur la rend dans le même appel : c'est le seul que la
   * barre fait au chargement.
   */
  modules?: {
    sirh: { actif: boolean; fonctionnalites: string[] };
    bdese: { actif: boolean };
  };
}

export const invitationsEmployeur = {
  /** Les entreprises où ce compte tient un rôle. Vide pour un cabinet. */
  async miens(): Promise<MonEmployeur[]> {
    return extraire(await api.get('/employeurs/miens'));
  },

  async lister(employerUuid: string): Promise<InvitationEmployeur[]> {
    return extraire(await api.get(`/employeurs/${employerUuid}/invitations`));
  },

  async inviter(params: {
    employerUuid: string;
    email: string;
    role: RoleEmployeur;
  }): Promise<InvitationEmployeur> {
    return extraire(await api.post('/employeurs/invitations', params));
  },

  async revoquer(employerUuid: string, uuid: string): Promise<void> {
    await api.delete(`/employeurs/${employerUuid}/invitations/${uuid}`);
  },

  async lireParJeton(jeton: string): Promise<InvitationLue> {
    return extraire(
      await api.get(`/employeurs/invitations/par-jeton/${jeton}`)
    );
  },

  async accepter(jeton: string): Promise<{ role: RoleEmployeur }> {
    return extraire(
      await api.post(`/employeurs/invitations/par-jeton/${jeton}/accepter`)
    );
  },
};
